import React, { useState } from "react";
import { useMutation } from "graphql-hooks";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import useIsMobile from "./hooks/useIsMobile";
import ShoppingBagOutlined from "@mui/icons-material/ShoppingBagOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import MovieCreationOutlined from "@mui/icons-material/MovieCreationOutlined";
import SportsEsportsOutlined from "@mui/icons-material/SportsEsportsOutlined";
import MarkUnreadChatAltOutlined from "@mui/icons-material/MarkUnreadChatAltOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";

// Import images from assets/Home folder
import socialBannerSection from "./assets/Home/social-banner-section.png";
import heroBannerGirl from "./assets/Home/hero-banner-girl.png";
import image1 from "./assets/Home/image-1.png";
import image2 from "./assets/Home/image-2.png";
import buySellInfographic from "./assets/Home/buy-sell-infographic.png";
import joinTheFunMascotGreen from "./assets/Home/join-the-fun-mascot-green.png";
import shareStoriesInfographic from "./assets/Home/share-stories-infographic.png";
import joinTheFunMascotOrange from "./assets/Home/join-the-fun-mascot-orange.png";
import circularEcoInfographic from "./assets/Home/circular-eco-infographic.png";
import joinTheFunMascotBlue from "./assets/Home/join-the-fun-mascot-blue.png";
import joinTheFunMascotWhite from "./assets/Home/join-the-fun-mascot-white.png";
import joinTheFunMascotWhiteMobile from "./assets/Home/join-the-fun-mascot-white-mobile.png";
import mascotJoin from "./assets/Home/mascot-join.png";
import joinConnectWithGirl from "./assets/Home/join-connect-with-girl.png";

import { SAVE_WAITIG_LIST_EMAIL_MUTATION } from "./graphQL/waitlist_qry";

function Home() {
  const isMobile = useIsMobile(768); // Use 768px breakpoint for email popup
  const navigate = useNavigate();

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <Helmet>
        <title>R3PLAY - Toys meet content, commerce, and community</title>
        <meta
          name="description"
          content="Buy toys, sell toys, trade toys, replay toys, share stories about toys"
        />
        <meta
          property="keywords"
          content="Replay toys, resell toys, trade toys, tell stories about toys, toys social commerce, used toys commerce, collectible toys, nostalgia toys, nostalgic toys, share toy stories, share toy memories"
        />
      </Helmet>
      <Box
        sx={
          {
            /*maxWidth: "1440px"*/
          }
        }
      >
        {/* Header Section with Background Image */}
        <Box
          sx={{
            backgroundImage: `url(${socialBannerSection})`,
            "@media (max-width: 1360px)": {
              height: "549px",
            },
            "@media (max-width: 856px)": {
              height: "550px",
            },
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            height: "630px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {/* Mascot Hero - Bottom Left */}
          <Box
            sx={{
              position: "absolute",
              bottom: "0px",
              left: "-40px",
              cursor: "pointer",
              zIndex: 8,
              "@media (max-width: 1360px)": {
                bottom: "0px",
              },
              "@media (max-width: 856px)": {
                display: "none",
              },
            }}
            onClick={() => navigate("/signup")}
          >
            <Box
              component="img"
              src={joinTheFunMascotWhite}
              alt="Join the Fun Mascot Hero"
              sx={{
                width: "207px",
                height: "auto",
              }}
            />
          </Box>
          {/* Mascot Hero - Bottom Left - mobile*/}
          <Box
            sx={{
              position: "absolute",
              bottom: "200px",
              left: "0px",
              cursor: "pointer",
              zIndex: 8,
              "@media (min-width: 856px)": {
                display: "none",
              },
            }}
            onClick={() => navigate("/signup")}
          >
            <Box
              component="img"
              src={joinTheFunMascotWhiteMobile}
              alt="Join the Fun Mascot Hero"
              sx={{
                width: "85px",
                height: "auto",
              }}
            />
          </Box>

          {/* Hero Banner Girl - Right Side */}
          <Box
            sx={{
              position: "absolute",
              right: "127px",
              bottom: "-44px",
              zIndex: 9,
              "@media (max-width: 1360px)": {
                right: "0px",
              },
              "@media (max-width: 856px)": {
                right: "0px",
                bottom: "-18px",
              },
            }}
          >
            <Box
              component="img"
              src={heroBannerGirl}
              alt="Hero Banner Girl"
              sx={{
                width: "637px",
                height: "562px",
                "@media (max-width: 1360px)": {
                  width: "484px",
                  height: "427px",
                },
                "@media (max-width: 856px)": {
                  width: "331px",
                  height: "292px",
                },
              }}
            />
          </Box>

          {/* Image 1 and Image 2 - Only show above 1360px */}
          <Box
            sx={{
              position: "absolute",
              right: "901px",
              bottom: "1px",
              display: "flex",
              gap: "20px",
              zIndex: 7,
              "@media (max-width: 1360px)": {
                display: "none",
              },
            }}
          >
            <Box
              component="img"
              src={image1}
              alt="Image 1"
              sx={{
                width: "auto",
                height: "78px",
              }}
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: "764px",
              bottom: "2px",
              display: "flex",
              gap: "20px",
              zIndex: 7,
              "@media (max-width: 1360px)": {
                display: "none",
              },
            }}
          >
            <Box
              component="img"
              src={image2}
              alt="Image 2"
              sx={{
                width: "130px",
                height: "auto",
              }}
            />
          </Box>

          {/* Feature Boxes with Icons */}
          {/* Stories Box */}
          <Box
            sx={{
              position: "absolute",
              top: "90px",
              right: "600px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: "140px",
              height: "78px",
              "@media (max-width: 1360px)": {
                display: "none",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#01579B",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoStoriesOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "16px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Stories
              <br />
              <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                Share your stories
              </span>
            </Typography>
          </Box>

          {/* Video Box */}
          <Box
            sx={{
              position: "absolute",
              top: "160px",
              right: "155px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: "140px",
              height: "78px",
              "@media (max-width: 1360px)": {
                display: "none",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#EFB561",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideocamOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "16px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Video
              <br />
              <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                Share your videos
              </span>
            </Typography>
          </Box>

          {/* Toy Box */}
          <Box
            sx={{
              position: "absolute",
              top: "380px",
              right: "720px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: "140px",
              height: "78px",
              "@media (max-width: 1360px)": {
                display: "none",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#04A15B",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "16px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Toy
              <br />
              <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                Share your experience
              </span>
            </Typography>
          </Box>

          {/* Donate Box */}
          <Box
            sx={{
              position: "absolute",
              top: "320px",
              right: "20px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: "140px",
              height: "78px",
              "@media (max-width: 1360px)": {
                display: "none",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#EF6C00",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HandshakeOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "16px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Donate
              <br />
              <span style={{ fontSize: "12px", fontWeight: "normal" }}>
                Gift your toys
              </span>
            </Typography>
          </Box>

          {/* Tablet Version (1360px - 857px) - Show boxes with title only */}
          {/* Stories Box - Tablet */}
          <Box
            sx={{
              position: "absolute",
              top: "90px",
              right: "150px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "8px 12px",
              display: "none",
              alignItems: "center",
              gap: 1,
              width: "110px",
              height: "56px",
              "@media (max-width: 1360px) and (min-width: 857px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#01579B",
                color: "white",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoStoriesOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "14px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Stories
            </Typography>
          </Box>

          {/* Video Box - Tablet */}
          <Box
            sx={{
              position: "absolute",
              top: "200px",
              right: "55px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "8px 12px",
              display: "none",
              alignItems: "center",
              gap: 1,
              width: "110px",
              height: "56px",
              "@media (max-width: 1360px) and (min-width: 857px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#EFB561",
                color: "white",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideocamOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "14px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Video
            </Typography>
          </Box>

          {/* Toy Box - Tablet */}
          <Box
            sx={{
              position: "absolute",
              top: "370px",
              right: "440px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "8px 12px",
              display: "none",
              alignItems: "center",
              gap: 1,
              width: "110px",
              height: "56px",
              "@media (max-width: 1360px) and (min-width: 857px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#04A15B",
                color: "white",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "14px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Toy
            </Typography>
          </Box>

          {/* Donate Box - Tablet */}
          <Box
            sx={{
              position: "absolute",
              top: "320px",
              right: "10px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              padding: "8px 12px",
              display: "none",
              alignItems: "center",
              gap: 1,
              width: "110px",
              height: "56px",
              "@media (max-width: 1360px) and (min-width: 857px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#EF6C00",
                color: "white",
                borderRadius: "6px",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HandshakeOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                color: "black",
                fontSize: "14px",
                fontWeight: "bold",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Donate
            </Typography>
          </Box>

          {/* Mobile Version (below 856px) - Icon only boxes */}
          {/* Stories Box - Mobile */}
          <Box
            sx={{
              position: "absolute",
              top: "340px",
              right: "250px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: "56px",
              height: "56px",
              "@media (max-width: 856px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#01579B",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AutoStoriesOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          {/* Video Box - Mobile */}
          <Box
            sx={{
              position: "absolute",
              top: "290px",
              right: "65px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: "56px",
              height: "56px",
              "@media (max-width: 856px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#EFB561",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideocamOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          {/* Toy Box - Mobile */}
          <Box
            sx={{
              position: "absolute",
              bottom: "70px",
              right: "310px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: "56px",
              height: "56px",
              "@media (max-width: 856px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#04A15B",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          {/* Donate Box - Mobile */}
          <Box
            sx={{
              position: "absolute",
              top: "370px",
              right: "10px",
              zIndex: 6,
              backgroundColor: "white",
              opacity: 1,
              borderRadius: "12px",
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              width: "56px",
              height: "56px",
              "@media (max-width: 856px)": {
                display: "flex",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "#EF6C00",
                color: "white",
                borderRadius: "6px",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HandshakeOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          {/* Text Content */}
          <Container
            maxWidth={false}
            sx={{
              position: "absolute",
              left: "90px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              textAlign: "left",
              "@media (max-width: 1360px)": {
                pl: 3,
                pr: 0,
                left: "0px",
                top: "250px",
              },
              "@media (max-width: 856px)": {
                pl: 2,
                pr: 2,
                top: "60px",
                transform: "none",
                textAlign: "center",
              },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "64px",
                "@media (max-width: 1360px)": {
                  fontSize: "56px",
                },
                "@media (max-width: 856px)": {
                  fontSize: "40px",
                },
                fontWeight: "regular",
                fontFamily: "Coiny, cursive",
                color: "white",
                mb: 1,
                lineHeight: 1.1,
                filter: "drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))",
              }}
            >
              R3PLAY is live!
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "24px",
                color: "white",
                mb: 3,
                maxWidth: "680px",
                "@media (max-width: 1360px)": {
                  maxWidth: "450px",
                  fontSize: "20px",
                },
                "@media (max-width: 856px)": {
                  fontSize: "16px",
                  maxWidth: "100%",
                },
                lineHeight: 1.4,
                filter: "drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))",
              }}
            >
              Explore and post videos, stories, and photos while we get the
              marketplace ready. Buying, selling, trading, and donating are
              coming soon.
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate("/signup")}
              sx={{
                backgroundColor: "#EF5658",
                color: "white",
                borderRadius: "4px",
                fontSize: "15px",
                fontWeight: 500,
                textTransform: "uppercase",
                width: "240px",
                height: "50px",
                "&:hover": {
                  backgroundColor: "#D84A4C",
                },
              }}
            >
              YES, I WANT TO SIGN UP!
            </Button>
          </Container>
        </Box>

        {/* Buy Sell Trade Section */}
        <Box
          sx={{
            backgroundColor: "#E5F5EC",
            minHeight: "880px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            py: 6,
            "@media (max-width: 856px)": {
              pb: 3,
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              mx: "auto",
              "@media (max-width: 1360px)": {
                px: 4,
              },
              "@media (max-width: 856px)": {
                px: 2,
              },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              {/* Left side - Infographic Image */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  pl: 7,
                  "@media (max-width: 1360px)": {
                    order: 1,
                    pl: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Box
                    component="img"
                    src={buySellInfographic}
                    alt="Buy Sell Trade Infographic"
                    sx={{
                      maxWidth: "100%",
                      width: "531px",
                      height: "auto",
                      borderRadius: "12px",
                      "@media (max-width: 856px)": {
                        width: "241px",
                      },
                    }}
                  />
                </Box>
              </Grid>

              {/* Right side - Content */}
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  pr: 15,
                  pb: 9,
                  "@media (max-width: 1360px)": {
                    order: 2,
                    pb: 18,
                    pr: 10,
                    paddingLeft: "100px !important",
                  },
                  "@media (max-width: 856px) and (min-width: 768px)": {
                    order: 2,
                    paddingLeft: "68px !important",
                    paddingRight: "38px !important",
                  },
                  "@media (max-width: 767px)": {
                    order: 2,
                    paddingLeft: "30px !important",
                    pr: 0,
                  },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "48px",
                    fontWeight: "regular",
                    fontFamily: "Coiny, cursive",
                    color: "#04A15B",
                    mb: 3,
                    lineHeight: 1.1,
                    "@media (max-width: 856px)": {
                      fontSize: "36px",
                    },
                  }}
                >
                  Buy. Sell. Trade. Replay.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "16px",
                    color: "#666",
                    mb: 2,
                    lineHeight: 1.5,
                    pr: 15,
                    "@media (max-width: 856px)": {
                      pr: 0,
                    },
                  }}
                >
                  This isn't your average toy store. It's a dynamic space where
                  community meets commerce.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "16px",
                    color: "#666",
                    mb: 3,
                    lineHeight: 1.5,
                  }}
                >
                  Collect what you love. Pass on what you don't.
                </Typography>

                {/* Feature Cards */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    pl: 0,
                    pr: 0,
                  }}
                >
                  {/* For Buyers */}
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
                      borderRadius: 3,
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #04A15B",
                        borderRadius: 2,
                        width: "52px",
                        height: "52px",
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        flexShrink: 0,
                      }}
                    >
                      <ShoppingBagOutlined
                        sx={{ color: "#04A15B", fontSize: 28 }}
                      />
                    </Box>
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          fontFamily: "Coiny, cursive",
                          letterSpacing: "0.04em",
                          mb: 1,
                          color: "#333",
                        }}
                      >
                        For Buyers
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.4 }}
                      >
                        Score everything from classic characters to quirky
                        finds. Easy filters and trusted sellers make it simple
                        to discover your next obsession.
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* For Sellers */}
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
                      borderRadius: 3,
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #04A15B",
                        borderRadius: 2,
                        width: "52px",
                        height: "52px",
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        flexShrink: 0,
                      }}
                    >
                      <Inventory2Outlined
                        sx={{ color: "#04A15B", fontSize: 28 }}
                      />
                    </Box>
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          fontFamily: "Coiny, cursive",
                          letterSpacing: "0.04em",
                          mb: 1,
                          color: "#333",
                        }}
                      >
                        For Sellers
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.4 }}
                      >
                        Your toys still have game. List them in just a few taps
                        and connect with buyers who truly get it.
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* For Traders */}
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
                      borderRadius: 3,
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #04A15B",
                        borderRadius: 2,
                        width: "52px",
                        height: "52px",
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        flexShrink: 0,
                      }}
                    >
                      <StorefrontOutlined
                        sx={{ color: "#04A15B", fontSize: 28 }}
                      />
                    </Box>
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          fontFamily: "Coiny, cursive",
                          letterSpacing: "0.04em",
                          mb: 1,
                          color: "#333",
                        }}
                      >
                        For Traders
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.4, mb: 1 }}
                      >
                        Have something cool but want something new? Trade
                        directly with others — no cash needed, just a shared
                        love of play.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.4 }}
                      >
                        Because one person's past favorite is another's future
                        delight.
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>

          {/* Clickable Join-the-fun-mascot in bottom right corner */}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              right: "-40px",
              cursor: "pointer",
            }}
            onClick={() => navigate("/signup")}
          >
            <Box
              component="img"
              src={joinTheFunMascotGreen}
              alt="Join the Fun Mascot"
              sx={{
                width: "207px",
                height: "auto",
                "@media (max-width: 856px)": {
                  width: "140px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Share Stories Section */}
        <Box
          sx={{
            backgroundColor: "#FCF2E3",
            minHeight: "800px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            py: 6,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              mx: "auto",
              "@media (max-width: 1360px)": {
                px: 4,
              },
              "@media (max-width: 856px)": {
                px: 2,
              },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              {/* Left side - Content */}
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  "@media (max-width: 1360px)": {
                    order: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    pl: 8,
                    pb: 9,
                    "@media (max-width: 1360px)": {
                      pl: 8,
                      pr: 7,
                      pb: 18,
                    },
                    "@media (max-width: 856px) and (min-width: 768px)": {
                      pl: 4,
                      pr: 4,
                      pb: 14,
                    },
                    "@media (max-width: 767px)": {
                      pl: 0,
                      pr: 0,
                      pb: 14,
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "48px",
                      fontWeight: "bold",
                      fontFamily: "Coiny, cursive",
                      color: "#EFB561",
                      mb: 3,
                      lineHeight: 1.1,
                      "@media (max-width: 856px)": {
                        fontSize: "36px",
                      },
                    }}
                  >
                    Share Stories. Spark Joy.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "#666",
                      mb: 2,
                      pr: 20,
                      lineHeight: 1.5,
                      "@media (max-width: 1360px)": {
                        pr: 0,
                      },
                    }}
                  >
                    Whether you're a toy collector, a nostalgia hunter, or just
                    craving some retro feels — this is your memory lane.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "#666",
                      mb: 2,
                      pr: 20,
                      lineHeight: 1.5,
                      "@media (max-width: 1360px)": {
                        pr: 0,
                      },
                    }}
                  >
                    Relive the magic. Share your moments. And connect with
                    people who totally get it.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "#666",
                      mb: 4,
                      pr: 20,
                      lineHeight: 1.5,
                      "@media (max-width: 1360px)": {
                        pr: 0,
                      },
                    }}
                  >
                    Dive into iconic characters, heartfelt throwbacks, and
                    story-driven fun. This is where toys talk back.
                  </Typography>

                  {/* Feature List */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {/* Feature 1 */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          border: "2px solid #EFB561",
                          borderRadius: 1,
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        <MovieCreationOutlined
                          sx={{ color: "#EFB561", fontSize: 24 }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          lineHeight: 1.4,
                          pr: 25,
                          "@media (max-width: 1360px)": {
                            pr: 0,
                          },
                        }}
                      >
                        Watch toys come alive through unboxings, reviews, and
                        fan-made videos
                      </Typography>
                    </Box>

                    {/* Feature 2 */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          border: "2px solid #EFB561",
                          borderRadius: 1,
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        <SportsEsportsOutlined
                          sx={{ color: "#EFB561", fontSize: 24 }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          lineHeight: 1.4,
                          pr: 25,
                          "@media (max-width: 1360px)": {
                            pr: 0,
                          },
                        }}
                      >
                        Post your own toy stories with photos, memories, and
                        collection highlights
                      </Typography>
                    </Box>

                    {/* Feature 3 */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          border: "2px solid #EFB561",
                          borderRadius: 1,
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        <MarkUnreadChatAltOutlined
                          sx={{ color: "#EFB561", fontSize: 24 }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          lineHeight: 1.4,
                          pr: 25,
                          "@media (max-width: 1360px)": {
                            pr: 0,
                          },
                        }}
                      >
                        Chat it up with collectors and fellow fans in a space
                        built for storytelling and connection
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Right side - Infographic Image */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  pl: 0,
                  "@media (max-width: 1360px)": {
                    order: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Box
                    component="img"
                    src={shareStoriesInfographic}
                    alt="Share Stories Infographic"
                    sx={{
                      maxWidth: "100%",
                      width: "531px",
                      height: "auto",
                      borderRadius: "12px",
                      "@media (max-width: 856px)": {
                        width: "304px",
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>

          {/* Clickable Join-the-fun-mascot-orange in bottom left corner */}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: "-40px",
              cursor: "pointer",
            }}
            onClick={() => navigate("/signup")}
          >
            <Box
              component="img"
              src={joinTheFunMascotOrange}
              alt="Join the Fun Mascot Orange"
              sx={{
                width: "207px",
                height: "auto",
                "@media (max-width: 856px)": {
                  width: "140px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Circular Play Section */}
        <Box
          sx={{
            backgroundColor: "#E1F5FE",
            minHeight: "700px",
            "@media (max-width: 1360px)": {
              minHeight: "600px",
              pt: 6,
              pb: 10,
            },
            position: "relative",
            display: "flex",
            alignItems: "center",
            py: 6,
            "@media (max-width: 856px)": {
              pt: 6,
              pb: 8,
            },
          }}
        >
          <Container maxWidth={false}>
            <Grid container spacing={4} alignItems="center">
              {/* Left side - Infographic Image */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    "@media (max-width: 856px)": {
                      px: 2,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={circularEcoInfographic}
                    alt="Circular Eco Infographic"
                    sx={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "12px",
                      "@media (max-width: 856px)": {
                        width: "272px",
                      },
                    }}
                  />
                </Box>
              </Grid>

              {/* Right side - Content */}
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    pr: 6,
                    "@media (max-width: 1360px)": {
                      order: 2,
                      pr: 5,
                      pb: 10,
                      paddingLeft: "60px !important",
                    },
                    "@media (max-width: 856px) and (min-width: 768px)": {
                      order: 2,
                      paddingLeft: "38px !important",
                      paddingRight: "38px !important",
                    },
                    "@media (max-width: 767px)": {
                      order: 2,
                      paddingLeft: "0px !important",
                      pr: 0,
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "48px",
                      fontWeight: "bold",
                      fontFamily: "Coiny, cursive",
                      color: "#01579B",
                      mb: 2,
                      lineHeight: 1.1,
                      "@media (max-width: 856px)": {
                        fontSize: "36px",
                      },
                    }}
                  >
                    Circular Play, Endless Joy
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      fontFamily: "Coiny, cursive",
                      letterSpacing: "0.04em",
                      color: "#333",
                      mb: 3,
                    }}
                  >
                    Give Toys a Second Life
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "rgba(0, 0, 0, 0.87)",
                      mb: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    We're reimagining sustainability through storytelling and
                    play.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "rgba(0, 0, 0, 0.87)",
                      mb: 2,
                      lineHeight: 1.5,
                      pr: 15,
                      "@media (max-width: 856px)": {
                        pr: 0,
                      },
                    }}
                  >
                    Every time you buy, sell, trade, or donate on R3PLAY, you're
                    keeping toys out of landfills — and putting them into loving
                    hands.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "rgba(0, 0, 0, 0.87)",
                      mb: 4,
                      lineHeight: 1.5,
                      pr: 15,
                      "@media (max-width: 856px)": {
                        pr: 0,
                      },
                    }}
                  >
                    It's more than reuse — it's a joyful loop where toys live
                    on, memories multiply, and the planet gets a little happier.
                  </Typography>

                  {/* Join the Movement Section */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <PublicOutlined sx={{ color: "#01579B", fontSize: 28 }} />

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Coiny, cursive",
                        letterSpacing: "0.04em",
                        color: "#01579B",
                        fontSize: "1.3rem",
                      }}
                    >
                      Join the Movement
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      color: "rgba(0, 0, 0, 0.87)",
                      lineHeight: 1.5,
                      pr: 15,
                      "@media (max-width: 856px)": {
                        pr: 0,
                      },
                    }}
                  >
                    Every toy you pass on or adopt makes a difference. Together,
                    we build a future that's playful and sustainable.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>

          {/* Clickable Join-the-fun-mascot-blue in bottom right corner */}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              right: "-40px",
              cursor: "pointer",
            }}
            onClick={() => navigate("/signup")}
          >
            <Box
              component="img"
              src={joinTheFunMascotBlue}
              alt="Join the Fun Mascot Blue"
              sx={{
                width: "207px",
                height: "auto",
                "@media (max-width: 856px)": {
                  width: "140px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Blue Header Section for Tablet */}
        <Box
          sx={{
            display: "none",
            "@media (max-width: 1410px)": {
              display: "block",
              backgroundColor: "#01579B",
              py: 4,
              textAlign: "center",
            },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: "28px",
              fontWeight: "bold",
              fontFamily: "Coiny, cursive",
              letterSpacing: "0.02em",
              color: "white",
              mb: 1,
              lineHeight: 1.2,
              "@media (max-width: 856px)": {
                fontSize: "24px",
              },
            }}
          >
            GET STARTED
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: "30px",
              fontWeight: "bold",
              fontFamily: "Coiny, cursive",
              color: "white",
              letterSpacing: "0.02em",
              lineHeight: 1.1,
              "@media (max-width: 856px)": {
                fontSize: "28px",
              },
            }}
          >
            BE PART OF THE PLAY
          </Typography>
        </Box>

        {/* Join the Fun Section */}
        <Box
          data-section="join-the-fun"
          sx={{
            minHeight: "400px",
            position: "relative",
            width: "100%",
            overflow: "hidden",
            "@media (max-width: 1410px)": {
              minHeight: "300px",
            },
            "@media (max-width: 856px)": {
              minHeight: "400px",
            },
          }}
        >
          {/* Blue Background - Full width with clipPath */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#01579B", // Blue color
              clipPath: "polygon(0 0, 50% 0, 30% 100%, 0 100%)", // 50% top, 30% bottom
              zIndex: 1,
              "@media (max-width: 1410px)": {
                display: "none", // Remove blue background on tablet
              },
            }}
          />

          {/* Orange Background - Full width with clipPath */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#FDCE67", // Orange color
              clipPath: "polygon(50% 0, 100% 0, 100% 100%, 30% 100%)", // 50% top, 70% bottom
              zIndex: 1,
              "@media (max-width: 1410px)": {
                clipPath: "none", // Remove diagonal cut, keep full orange background
              },
            }}
          />

          {/* Content Container */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100%",
              display: "flex",
              "@media (max-width: 1410px)": {
                flexDirection: "column",
                height: "330px",
                justifyContent: "center",
              },
              "@media (max-width: 856px)": {
                height: "320px",
                justifyContent: "center",
              },
            }}
          >
            {/* Left Side Content - Blue Section */}
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "center",
                position: "relative",
                "@media (max-width: 1410px)": {
                  display: "none", // Hide blue section content on tablet
                },
              }}
            >
              {/* Mascot Join Image - Left Side */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  height: "70%",
                  "@media (max-width: 1410px)": {
                    display: "none", // Hide mascot on tablet
                  },
                }}
              >
                <img
                  src={mascotJoin}
                  alt="Mascot Join"
                  style={{
                    width: "120px",
                    height: "auto",
                  }}
                />
              </Box>

              {/* Text Content - Center */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  maxWidth: "450px",
                  flex: 1,
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: "28px",
                    fontWeight: "regular",
                    fontFamily: "Coiny, cursive",
                    color: "white",
                    mb: 1,
                    letterSpacing: "0.02em",
                  }}
                >
                  GET STARTED
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "34px",
                    fontWeight: "regular",
                    fontFamily: "Coiny, cursive",
                    color: "white",
                    letterSpacing: "0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  BE PART OF THE PLAY
                </Typography>
              </Box>
            </Box>

            {/* Right Side Content - Orange Section */}
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                "@media (max-width: 1410px)": {
                  width: "100%", // Full width on tablet
                  height: "100%", // Full height since blue section is hidden
                },
              }}
            >
              <Container
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Join Connect With Girl Image */}
                <Box
                  sx={{
                    mt: 5,
                    mr: 12,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    "@media (max-width: 1410px)": {
                      mt: 0,
                      mr: 0,
                    },
                    "@media (max-width: 856px)": {
                      mt: 10,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={joinConnectWithGirl}
                    alt="Join Connect With Girl"
                    sx={{
                      width: "447px",
                      height: "auto",
                      "@media (max-width: 1410px)": {
                        width: "370px",
                      },
                      "@media (max-width: 856px)": {
                        width: "361px",
                      },
                    }}
                  />
                </Box>

                {/*  I WANT TO SIGN UP Button */}
                <Box
                  sx={{
                    width: "400px",
                    "@media (max-width: 1410px)": {
                      width: "300px",
                    },
                    "@media (max-width: 856px)": {
                      width: "330px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "327px",
                      height: "70px",
                      backgroundColor: "white",
                      borderRadius: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigate("/signup")}
                      fullWidth
                      sx={{
                        backgroundColor: "#01579B",
                        color: "white",
                        borderRadius: "50px",
                        fontSize: "16px",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        width: "309px",
                        height: "56px",
                        "&:hover": {
                          backgroundColor: "#1565c0",
                        },
                      }}
                    >
                      YES, I WANT TO SIGN UP!
                    </Button>
                  </Box>
                </Box>
              </Container>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Home;
